import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { usePractice } from '../context/PracticeContext';

const EVENT_CATEGORIES = {
  // GH-78: PATIENT_SEARCHED and PATIENT_VIEWED both showed as "Patient View",
  // making the filter dropdown's "Patient Viewed" option seem broken — it
  // narrowed to PATIENT_VIEWED rows, but the user expected it to include
  // the PATIENT_SEARCHED rows that displayed identically. Distinguish them.
  'PATIENT_SEARCHED': { label: 'Patient List Viewed', category: 'patient' },
  'PATIENT_VIEWED': { label: 'Patient Viewed', category: 'patient' },
  'PATIENT_CREATED': { label: 'Patient Created', category: 'patient' },
  'PATIENT_UPDATED': { label: 'Patient Updated', category: 'patient' },
  'CONSENT_OPT_IN': { label: 'Consent Opt-In', category: 'consent' },
  'CONSENT_OPT_OUT': { label: 'Consent Opt-Out', category: 'consent' },
  'SMS_SENT': { label: 'SMS Sent', category: 'messaging' },
  'EMAIL_SENT': { label: 'Email Sent', category: 'messaging' },
  'VOICE_CALL_MADE': { label: 'Voice Call', category: 'messaging' },
  'NOTIFICATION_RULE_CREATED': { label: 'Rule Created', category: 'notification' },
  'NOTIFICATION_RULE_UPDATED': { label: 'Rule Updated', category: 'notification' },
  'CAMPAIGN_ENROLLED': { label: 'Campaign Enroll', category: 'campaign' },
  'CAMPAIGN_COMPLETED': { label: 'Campaign Done', category: 'campaign' },
  'VOICE_CALL_STARTED': { label: 'Call Started', category: 'voice' },
  'VOICE_CALL_COMPLETED': { label: 'Call Completed', category: 'voice' },
};

const CATEGORY_COLORS = {
  patient: { bg: '#eff6ff', color: '#1d4ed8' },
  consent: { bg: '#fef3c7', color: '#92400e' },
  messaging: { bg: '#ecfdf5', color: '#065f46' },
  notification: { bg: '#f5f3ff', color: '#5b21b6' },
  campaign: { bg: '#fdf2f8', color: '#9d174d' },
  voice: { bg: '#fff7ed', color: '#9a3412' },
};

const SENSITIVITY_COLORS = {
  LOW: { bg: '#f0fdf4', color: '#166534' },
  MEDIUM: { bg: '#fefce8', color: '#854d0e' },
  HIGH: { bg: '#fef2f2', color: '#991b1b' },
};

function ActivityLog() {
  const { selectedPracticeId } = usePractice();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({
    event_type: '',
    resource_type: '',
    start_date: '',
    end_date: '',
    q: '',
  });
  const limit = 50;

  const loadLogs = useCallback(async () => {
    if (!selectedPracticeId) return;
    try {
      setLoading(true);
      setError(null);
      const params = { limit, offset: page * limit };
      if (filters.event_type) params.event_type = filters.event_type;
      if (filters.resource_type) params.resource_type = filters.resource_type;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.q) params.q = filters.q;

      const res = await api.getPracticeAuditLog(selectedPracticeId, params);
      if (res.success) {
        setLogs(res.data || []);
        setTotal(res.pagination?.total || 0);
      }
    } catch (err) {
      setError('Failed to load activity log');
      console.error('Error loading audit log:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedPracticeId, page, filters]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const totalPages = Math.ceil(total / limit);

  if (!selectedPracticeId) {
    return (
      <div style={s.card}>
        <h2 style={s.title}>Activity Log</h2>
        <p style={{ color: '#6b7280', padding: 20 }}>Please select a practice to view activity logs.</p>
      </div>
    );
  }

  return (
    <div>
      <div style={s.headerRow}>
        <div>
          <h2 style={s.title}>Activity Log</h2>
          <p style={s.subtitle}>HIPAA-compliant audit trail of all practice activity.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={s.hipaaShield}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2.5">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
          </span>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#166534' }}>HIPAA Compliant</span>
        </div>
      </div>

      {error && <div style={s.errorMsg}>{error}</div>}

      {/* Filters */}
      <div style={s.filterRow}>
        <input
          type="text"
          placeholder="Search actions..."
          value={filters.q}
          onChange={(e) => handleFilterChange('q', e.target.value)}
          style={s.searchInput}
        />
        <select
          value={filters.event_type}
          onChange={(e) => handleFilterChange('event_type', e.target.value)}
          style={s.select}
        >
          <option value="">All Events</option>
          <option value="PATIENT_SEARCHED">Patient List Viewed</option>
          <option value="PATIENT_VIEWED">Patient Viewed</option>
          <option value="PATIENT_CREATED">Patient Created</option>
          <option value="PATIENT_UPDATED">Patient Updated</option>
          <option value="CONSENT_OPT_IN">Consent Opt-In</option>
          <option value="CONSENT_OPT_OUT">Consent Opt-Out</option>
          <option value="SMS_SENT">SMS Sent</option>
          <option value="EMAIL_SENT">Email Sent</option>
          <option value="NOTIFICATION_RULE_CREATED">Rule Created</option>
          <option value="NOTIFICATION_RULE_UPDATED">Rule Updated</option>
          <option value="VOICE_CALL_STARTED">Voice Call</option>
        </select>
        <select
          value={filters.resource_type}
          onChange={(e) => handleFilterChange('resource_type', e.target.value)}
          style={s.select}
        >
          <option value="">All Resources</option>
          <option value="Patient">Patient</option>
          <option value="Consent">Consent</option>
          <option value="Notification">Notification</option>
          <option value="NotificationRule">Notification Rule</option>
          <option value="Campaign">Campaign</option>
        </select>
        <input
          type="date"
          value={filters.start_date}
          onChange={(e) => handleFilterChange('start_date', e.target.value)}
          style={{ ...s.select, minWidth: 120 }}
          title="Start date"
        />
        <input
          type="date"
          value={filters.end_date}
          onChange={(e) => handleFilterChange('end_date', e.target.value)}
          style={{ ...s.select, minWidth: 120 }}
          title="End date"
        />
      </div>

      {/* Log entries */}
      <div style={s.card}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Loading...</div>
        ) : logs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
            No activity log entries found. Events are logged as you use the system.
          </div>
        ) : (
          <>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Timestamp</th>
                  <th style={s.th}>Event</th>
                  <th style={s.th}>Action</th>
                  <th style={s.th}>Resource</th>
                  <th style={s.th}>User</th>
                  <th style={s.th}>Sensitivity</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const eventInfo = EVENT_CATEGORIES[log.event_type] || { label: log.event_type, category: 'patient' };
                  const catColor = CATEGORY_COLORS[eventInfo.category] || CATEGORY_COLORS.patient;
                  const senColor = SENSITIVITY_COLORS[log.sensitivity_level] || SENSITIVITY_COLORS.MEDIUM;
                  return (
                    <tr key={log.id} style={s.tr}>
                      <td style={s.td}>
                        <div style={{ fontSize: 12, color: '#374151', whiteSpace: 'nowrap' }}>
                          {new Date(log.event_timestamp).toLocaleDateString()}
                        </div>
                        <div style={{ fontSize: 11, color: '#9ca3af' }}>
                          {new Date(log.event_timestamp).toLocaleTimeString()}
                        </div>
                      </td>
                      <td style={s.td}>
                        <span style={{
                          padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                          background: catColor.bg, color: catColor.color, whiteSpace: 'nowrap',
                        }}>
                          {eventInfo.label}
                        </span>
                      </td>
                      <td style={{ ...s.td, maxWidth: 350 }}>
                        <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.4 }}>
                          {log.action}
                        </div>
                        {log.change_summary && (
                          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                            {log.change_summary}
                          </div>
                        )}
                      </td>
                      <td style={s.td}>
                        {log.resource_type && (
                          <div style={{ fontSize: 12, color: '#6b7280' }}>
                            {log.resource_type}
                            {log.resource_name ? `: ${log.resource_name}` : ''}
                          </div>
                        )}
                      </td>
                      <td style={s.td}>
                        <div style={{ fontSize: 12, color: '#374151' }}>
                          {log.user_first_name ? `${log.user_first_name} ${log.user_last_name || ''}`.trim() : log.user_email || 'System'}
                        </div>
                      </td>
                      <td style={s.td}>
                        <span style={{
                          padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                          background: senColor.bg, color: senColor.color,
                        }}>
                          {log.sensitivity_level || 'MEDIUM'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div style={s.pagination}>
                <button style={s.pageBtn} disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                  Previous
                </button>
                <span style={{ fontSize: 13, color: '#6b7280' }}>
                  Page {page + 1} of {totalPages} ({total} entries)
                </span>
                <button style={s.pageBtn} disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 },
  subtitle: { fontSize: 14, color: '#6b7280', margin: '4px 0 0' },
  hipaaShield: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 28, height: 28, borderRadius: 8, background: '#dcfce7',
  },
  errorMsg: {
    background: '#fef2f2', color: '#dc2626', padding: '10px 14px',
    borderRadius: 8, marginBottom: 16, fontSize: 13,
  },
  filterRow: { display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' },
  searchInput: {
    flex: 1, minWidth: 180, padding: '8px 12px', borderRadius: 8,
    border: '1px solid #d1d5db', fontSize: 14, outline: 'none',
  },
  select: {
    padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db',
    fontSize: 13, background: '#fff', outline: 'none', minWidth: 130,
  },
  card: {
    background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600,
    color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5,
    borderBottom: '1px solid #e5e7eb', background: '#f9fafb',
  },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '10px 14px', fontSize: 13, color: '#374151', verticalAlign: 'top' },
  pagination: {
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    gap: 16, padding: '14px 0',
  },
  pageBtn: {
    padding: '6px 14px', borderRadius: 6, border: '1px solid #d1d5db',
    background: '#fff', fontSize: 13, cursor: 'pointer',
  },
};

export default ActivityLog;
